import { Css, FormSectionLayout, useModal } from "@homebound/beam";
import { ObjectConfig, useFormStates } from "@homebound/form-state";
import { useMemo } from "react";
import { SaveReadyPlanLocationInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageLocationsV2Query,
  PlanPackageLocationsV2_LocationFragment,
  PlanPackageLocationsV2_PlanPackageFragment,
  usePlanPackageLocationsV2Query,
  useSavePlanPackageLocationsV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import { AddLocationModal } from "src/routes/libraries/plan-package-v2/components/locations/AddLocationModal";
import { LevelLocationsTable } from "src/routes/libraries/plan-package-v2/components/locations/LevelLocationsTable";
import {
  RoomFormValue,
  formatFullRoomNumber,
  groupRoomLocations,
  isNumberedLevel,
} from "src/routes/libraries/plan-package-v2/components/locations/locationUtils";
import { queryResult } from "src/utils";

type LocationsTabProps = { planPackageId: string; versionId: string };

export function LocationsTab(props: LocationsTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageLocationsV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage, levels, roomTypes }) => (
    <DataView planPackage={planPackage} levels={levels} roomTypes={roomTypes} refetch={() => query.refetch()} />
  ));
}

type DataViewProps = {
  planPackage: PlanPackageLocationsV2_PlanPackageFragment;
  levels: PlanPackageLocationsV2Query["levels"];
  roomTypes: PlanPackageLocationsV2Query["roomTypes"];
  refetch: ReturnType<typeof usePlanPackageLocationsV2Query>["refetch"];
};

function DataView(props: DataViewProps) {
  const { planPackage, levels, roomTypes, refetch } = props;
  const [save] = useSavePlanPackageLocationsV2Mutation();
  const navigateToRpav = useNavigateToRpav();
  const { openModal } = useModal();

  /** Single save path for every location write. */
  async function saveRpls(inputs: SaveReadyPlanLocationInput[], opts: { membershipChanged?: boolean } = {}) {
    const { data } = await save({ variables: { inputs } });
    const draftId = data?.saveReadyPlanLocations.readyPlanLocations.first?.readyPlan.aggregateDraft?.id;
    if (draftId && draftId !== planPackage.version.id) return navigateToRpav(draftId);
    if (opts.membershipChanged) {
      const { data: fresh } = await refetch();
      const freshDraftId = fresh?.planPackage?.aggregateDraft?.id;
      if (freshDraftId && freshDraftId !== planPackage.version.id) navigateToRpav(freshDraftId);
    }
  }

  const { getFormState } = useFormStates<RoomFormValue, PlanPackageLocationsV2_LocationFragment>({
    config: roomConfig,
    getId: (rpl) => rpl.id,
    map: (rpl) => ({
      id: rpl.id,
      roomTypeId: rpl.roomType?.id,
      marketingName: rpl.marketingName,
      roomNumber: rpl.roomNumber,
    }),
    autoSave: async (os) => {
      const changed = os.changedValue;
      await saveRpls([
        // The backend recalcs the room's Location from roomNumber whenever roomType changes
        { ...changed, ...("roomTypeId" in changed ? { roomNumber: os.roomNumber.value } : {}) },
      ]);
    },
  });

  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const groups = useMemo(() => groupRoomLocations(planPackage.roomLocations), [planPackage]);
  const lastNumberedGroup = groups.filter((group) => isNumberedLevel(group.location.name)).last;

  function isDuplicateFullRoomNumber(rplId: string, fullRoomNumber: number): boolean {
    return groups.some(({ location, rooms }) =>
      rooms.some(
        (room) =>
          room.id !== rplId &&
          formatFullRoomNumber(location.name, getFormState(room).roomNumber.value ?? 0) === fullRoomNumber,
      ),
    );
  }

  return (
    <div css={Css.mt4.$}>
      <FormSectionLayout
        title="Locations"
        description="Add the levels and rooms of this plan, and keep room numbers in sync with the construction documents."
        withAutoSave
        actions={[
          {
            label: "Add",
            icon: "plus",
            variant: "secondary",
            disabled,
            onClick: () =>
              openModal({
                content: (
                  <AddLocationModal
                    planPackageId={planPackage.id}
                    groups={groups}
                    levels={levels}
                    roomTypes={roomTypes}
                    saveRpls={saveRpls}
                  />
                ),
              }),
          },
        ]}
        initialFields={
          <div css={Css.df.fdc.gap3.w100.$}>
            {groups.isEmpty && <div>No locations found</div>}
            {groups.map((group) => (
              <LevelLocationsTable
                key={group.location.id}
                group={group}
                roomTypes={roomTypes}
                getFormState={getFormState}
                disabled={disabled}
                saveRpls={saveRpls}
                isDuplicateFullRoomNumber={isDuplicateFullRoomNumber}
                // Levels 1 and 2 can't be deleted while Level 3 exists, so only offer the last numbered level
                showLevelDelete={
                  !!group.levelRpl && (!isNumberedLevel(group.location.name) || group === lastNumberedGroup)
                }
              />
            ))}
          </div>
        }
      />
    </div>
  );
}

const roomConfig: ObjectConfig<RoomFormValue> = {
  id: { type: "value" },
  roomTypeId: { type: "value" },
  marketingName: { type: "value" },
  roomNumber: { type: "value" },
};
